import { createFocusTrap, safeFocus } from '@nulo/a11y';
import { createSwiper } from '@nulo/swiper';

/**
 * @extends {HTMLElement}
 */
class ProductMediaGallery extends HTMLElement {
  /** @type {import('swiper').Swiper | null} */
  #mainSwiper = null;

  /** @type {import('swiper').Swiper | null} */
  #thumbSwiper = null;

  /** @type {import('swiper').Swiper | null} */
  #zoomSwiper = null;

  /** @type {(() => void) | null} */
  #releaseZoomTrap = null;

  /** @type {HTMLElement | null} */
  #zoomTrigger = null;

  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {number} */
  #zoomIndex = 0;

  /** @type {HTMLElement[]} */
  #zoomThumbs = [];

  /** @type {MediaQueryList | null} */
  #desktopMq = null;

  /** @type {(() => void) | null} */
  #desktopMqListener = null;

  connectedCallback() {
    if (this.#abortController) return;

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.#desktopMq = window.matchMedia('(min-width: 992px)');
    this.#desktopMqListener = () => this.#syncThumbSwiper();
    this.#desktopMq.addEventListener('change', this.#desktopMqListener);

    this.#initSwipers();
    this.#bindMagnifier(signal);
    this.#bindZoom(signal);
    this.#bindProductVideo(signal);
  }

  disconnectedCallback() {
    if (this.#desktopMq && this.#desktopMqListener) {
      this.#desktopMq.removeEventListener('change', this.#desktopMqListener);
    }
    this.#desktopMq = null;
    this.#desktopMqListener = null;

    this.#mainSwiper?.destroy?.(true, true);
    this.#thumbSwiper?.destroy?.(true, true);
    this.#zoomSwiper?.destroy?.(true, true);
    this.#mainSwiper = null;
    this.#thumbSwiper = null;
    this.#zoomSwiper = null;

    this.#releaseZoomTrap?.();
    this.#releaseZoomTrap = null;
    this.#zoomThumbs = [];

    this.#abortController?.abort();
    this.#abortController = null;
  }

  reinit() {
    this.disconnectedCallback();
    this.connectedCallback();
  }

  #isDesktopLayout() {
    return this.#desktopMq?.matches ?? window.matchMedia('(min-width: 992px)').matches;
  }

  #syncThumbSwiper() {
    const wantsThumbs = this.#isDesktopLayout();
    const hasThumbs = Boolean(this.#thumbSwiper);
    if (wantsThumbs === hasThumbs) return;

    const realIndex = this.#mainSwiper?.realIndex ?? 0;
    this.#mainSwiper?.destroy?.(true, true);
    this.#thumbSwiper?.destroy?.(true, true);
    this.#mainSwiper = null;
    this.#thumbSwiper = null;
    this.#initSwipers();

    if (this.#mainSwiper && realIndex > 0) {
      if (typeof this.#mainSwiper.slideToLoop === 'function') {
        this.#mainSwiper.slideToLoop(realIndex, 0);
      } else {
        this.#mainSwiper.slideTo(realIndex, 0);
      }
    }
  }

  #initThumbSwiper() {
    if (this.#thumbSwiper || !this.#isDesktopLayout()) return;

    const thumbEl = this.querySelector('[data-thumb-swiper]');
    if (!(thumbEl instanceof HTMLElement)) return;

    const thumbPrev = this.querySelector('[data-thumb-prev]');
    const thumbNext = this.querySelector('[data-thumb-next]');
    const showThumbNav =
      this.querySelectorAll('.product-media-gallery__thumb-slide').length >
      Number(this.dataset.navThreshold || 5);

    this.#thumbSwiper = createSwiper({
      region: this.querySelector('[data-thumb-carousel]'),
      el: thumbEl,
      activeSlideClass: 'swiper-slide-visible',
      config: {
        direction: 'vertical',
        slidesPerView: 'auto',
        slidesPerGroup: 1,
        spaceBetween: 8,
        slideToClickedSlide: true,
        watchSlidesProgress: true,
        freeMode: false,
        watchOverflow: true,
        navigation: showThumbNav
          ? {
            nextEl: thumbNext,
            prevEl: thumbPrev,
          }
          : false,
      },
    });
  }

  #initSwipers() {
    const mainEl = this.querySelector('[data-main-swiper]');
    if (!(mainEl instanceof HTMLElement)) return;

    this.#initThumbSwiper();

    const mainPrev = this.querySelector('[data-main-prev]');
    const mainNext = this.querySelector('[data-main-next]');
    const showMainNav = mainPrev instanceof HTMLElement && mainNext instanceof HTMLElement;

    this.#mainSwiper = createSwiper({
      region: this.querySelector('[data-main-carousel]'),
      el: mainEl,
      activeSlideClass: 'swiper-slide-active',
      config: {
        loop: true,
        speed: 500,
        slidesPerView: 1,
        slidesPerGroup: 1,
        preloadImages: false,
        thumbs: this.#thumbSwiper ? { swiper: this.#thumbSwiper } : undefined,
        navigation: showMainNav
          ? {
            nextEl: mainNext,
            prevEl: mainPrev,
          }
          : false,
        on: {
          slideChange: (swiper) => {
            this.#zoomIndex = swiper.realIndex;
          },
        },
      },
    });
  }

  /**
   * @param {AbortSignal} signal
   */
  #bindMagnifier(signal) {
    const lens = this.querySelector('[data-magnifier-lens]');
    if (!(lens instanceof HTMLElement)) return;

    const magnifiers = this.querySelectorAll('[data-magnifier]');

    for (const magnifier of magnifiers) {
      if (!(magnifier instanceof HTMLElement)) continue;

      const imageUrl = magnifier.dataset.imageUrl;
      if (!imageUrl) continue;

      const showLens = (event) => {
        if (window.matchMedia('(max-width: 991px)').matches) return;

        const target = magnifier.querySelector('.product-media-gallery__image-wrap');
        if (!(target instanceof HTMLElement)) return;

        const rect = target.getBoundingClientRect();
        const clientX = event instanceof MouseEvent ? event.clientX : rect.left + rect.width / 2;
        const clientY = event instanceof MouseEvent ? event.clientY : rect.top + rect.height / 2;
        const x = ((clientX - rect.left) / rect.width) * 100;
        const y = ((clientY - rect.top) / rect.height) * 100;

        lens.hidden = false;
        lens.style.backgroundImage = `url(${imageUrl})`;
        lens.style.backgroundPosition = `${x}% ${y}%`;
      };

      const hideLens = () => {
        lens.hidden = true;
      };

      magnifier.addEventListener('mouseenter', showLens, { signal });
      magnifier.addEventListener('mousemove', showLens, { signal });
      magnifier.addEventListener('mouseleave', hideLens, { signal });
    }
  }

  /**
   * Update active state of zoom thumbnail buttons.
   * @param {number} activeIndex
   */
  #updateZoomThumbs(activeIndex) {
    if (!this.#zoomThumbs.length) {
      this.#zoomThumbs = Array.from(this.querySelectorAll('[data-zoom-thumb]'));
    }
    for (const thumb of this.#zoomThumbs) {
      const slideIndex = Number(thumb.dataset.slideIndex);
      const isActive = slideIndex === activeIndex;
      thumb.classList.toggle('product-media-gallery__zoom-thumb--active', isActive);
      thumb.setAttribute('aria-selected', isActive ? 'true' : 'false');
      if (isActive) {
        thumb.setAttribute('aria-current', 'true');
        thumb.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
      } else {
        thumb.removeAttribute('aria-current');
      }
    }
  }

  /**
   * Slide the zoom swiper to a specific slide index.
   * @param {number} index
   * @param {number} [speed=300]
   */
  #setZoomSwiperIndex(index, speed = 300) {
    this.#zoomIndex = index;
    if (this.#zoomSwiper) {
      if (this.#zoomSwiper.params?.loop) {
        this.#zoomSwiper.slideToLoop(index, speed);
      } else {
        this.#zoomSwiper.slideTo(index, speed);
      }
    }
    this.#updateZoomThumbs(index);
  }

  /**
   * Initialise Swiper for the zoom dialog so touch/swipe gestures and arrows work.
   */
  #initZoomSwiper() {
    if (this.#zoomSwiper) return;

    const zoomSwiperEl = this.querySelector('[data-zoom-swiper]');
    if (!(zoomSwiperEl instanceof HTMLElement)) return;

    const stage = this.querySelector('[data-zoom-stage]');
    const prevBtn = this.querySelector('[data-zoom-prev]');
    const nextBtn = this.querySelector('[data-zoom-next]');
    const zoomSlidesCount = zoomSwiperEl.querySelectorAll('.swiper-slide').length;
    const shouldLoop = zoomSlidesCount > 1;

    this.#zoomSwiper = createSwiper({
      region: stage instanceof HTMLElement ? stage : undefined,
      el: zoomSwiperEl,
      activeSlideClass: 'swiper-slide-active',
      config: {
        loop: shouldLoop,
        speed: 300,
        slidesPerView: 1,
        slidesPerGroup: 1,
        navigation: shouldLoop && prevBtn && nextBtn
          ? {
              nextEl: nextBtn,
              prevEl: prevBtn,
            }
          : false,
        on: {
          slideChange: (swiper) => {
            const activeIndex = shouldLoop ? swiper.realIndex : swiper.activeIndex;
            this.#zoomIndex = activeIndex;
            this.#updateZoomThumbs(activeIndex);
          },
        },
      },
    });

    if (zoomSlidesCount <= 1) {
      if (prevBtn instanceof HTMLElement) prevBtn.hidden = true;
      if (nextBtn instanceof HTMLElement) nextBtn.hidden = true;
    }
  }

  /**
   * @param {AbortSignal} signal
   */
  #bindZoom(signal) {
    const dialog = this.querySelector('[data-zoom-dialog]');
    if (!(dialog instanceof HTMLDialogElement)) return;

    const stage = this.querySelector('[data-zoom-stage]');
    const thumbsWrap = this.querySelector('[data-zoom-thumbs]');

    this.#zoomThumbs = Array.from(this.querySelectorAll('[data-zoom-thumb]'));
    this.#initZoomSwiper();

    const openZoom = (index, trigger) => {
      this.#zoomTrigger = trigger instanceof HTMLElement ? trigger : null;
      if (!this.#zoomSwiper) this.#initZoomSwiper();
      if (!dialog.open) dialog.showModal();

      // Ensure swiper calculates dimensions correctly now that the dialog is visible.
      this.#zoomSwiper?.update?.();
      this.#zoomSwiper?.navigation?.update?.();
      this.#setZoomSwiperIndex(index, 0);

      this.#releaseZoomTrap?.();
      this.#releaseZoomTrap = createFocusTrap(dialog, () => this.#closeZoom(dialog));

      const closeBtn = dialog.querySelector('.product-media-gallery__zoom-close');
      safeFocus(closeBtn instanceof HTMLElement ? closeBtn : dialog);
    };

    const openButtons = this.querySelectorAll('[data-zoom-open]');
    for (const button of openButtons) {
      button.addEventListener(
        'click',
        () => {
          const index = Number(button instanceof HTMLElement ? button.dataset.slideIndex : 0);
          openZoom(index, button instanceof HTMLElement ? button : null);
        },
        { signal },
      );
    }

    thumbsWrap?.addEventListener(
      'click',
      (event) => {
        const thumb = event.target instanceof Element ? event.target.closest('[data-zoom-thumb]') : null;
        if (!(thumb instanceof HTMLElement) || !thumbsWrap.contains(thumb)) return;
        const index = Number(thumb.dataset.slideIndex);
        this.#setZoomSwiperIndex(index);
      },
      { signal },
    );

    dialog.addEventListener(
      'click',
      (event) => {
        if (event.target === dialog) this.#closeZoom(dialog);
      },
      { signal },
    );

    dialog.addEventListener(
      'close',
      () => {
        this.#releaseZoomTrap?.();
        this.#releaseZoomTrap = null;
        safeFocus(this.#zoomTrigger);
        this.#zoomTrigger = null;
      },
      { signal },
    );

    stage?.addEventListener(
      'keydown',
      (event) => {
        if (!(event instanceof KeyboardEvent)) return;
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.#zoomSwiper?.slideNext();
        } else if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.#zoomSwiper?.slidePrev();
        }
      },
      { signal },
    );
  }


  /**
   * @param {HTMLDialogElement} dialog
   */
  #closeZoom(dialog) {
    if (dialog.open) dialog.close();
  }

  /**
   * @param {AbortSignal} signal
   */
  #bindProductVideo(signal) {
    if (this.dataset.hasProductVideo !== 'true') return;

    const dialog = this.querySelector('[data-product-video-dialog]');
    if (!(dialog instanceof HTMLDialogElement)) return;

    const iframe = this.querySelector('[data-product-video-iframe]');
    const nativeVideo = this.querySelector('.product-media-gallery__video-native');
    const videoMode = this.dataset.videoMode || '';
    const embedUrl = this.dataset.videoEmbed || '';
    let videoTrigger = null;

    const resetNativeVideo = () => {
      if (!(nativeVideo instanceof HTMLVideoElement)) return;
      nativeVideo.pause();
      nativeVideo.currentTime = 0;
    };

    const openVideo = (trigger) => {
      videoTrigger = trigger instanceof HTMLElement ? trigger : null;

      if (videoMode === 'embed' && iframe instanceof HTMLIFrameElement && embedUrl) {
        iframe.src = embedUrl;
      }

      if (!dialog.open) dialog.showModal();

      if (videoMode !== 'embed' && nativeVideo instanceof HTMLVideoElement) {
        nativeVideo.currentTime = 0;
        const playPromise = nativeVideo.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(() => { });
        }
      }
    };

    const openButtons = this.querySelectorAll('[data-product-video-open]');
    for (const button of openButtons) {
      button.addEventListener(
        'click',
        () => openVideo(button instanceof HTMLElement ? button : null),
        { signal },
      );
    }

    dialog.addEventListener(
      'click',
      (event) => {
        if (event.target === dialog) dialog.close();
      },
      { signal },
    );

    dialog.addEventListener(
      'close',
      () => {
        if (iframe instanceof HTMLIFrameElement) {
          iframe.src = '';
        }

        resetNativeVideo();
        safeFocus(videoTrigger);
        videoTrigger = null;
      },
      { signal },
    );
  }
}

if (!customElements.get('product-media-gallery')) {
  customElements.define('product-media-gallery', ProductMediaGallery);
}